import { Injectable, signal } from '@angular/core';

/** How the judge supplies the outgoing document in Review & Sign. */
export type OutgoingSource = 'upload' | 'sign-attach';

/**
 * Per-session memory of the judge's outgoing-document choices in Review & Sign.
 * Bound to one filing at a time by `JudgeReviewDataService.setJudgeReviewFiling`;
 * binding a different filing drops everything remembered for the previous one.
 */
@Injectable({
  providedIn: 'root',
})
export class JudgeReviewOutgoingChoiceService {
  /**
   * Filing whose Review & Sign session is remembering an explicit Upload or
   * Sign-and-attach choice. Kept across `JudgeReviewDataService.clear` so Back from
   * Confirm restores that choice. Replaced when a different filing is loaded. A
   * Sign default is not recorded here, so the next open of the same filing still
   * starts on Sign until the judge picks another source.
   */
  private filingId: string | null = null;

  /** `${filingId}:${documentId}` → the source the judge picked this session. */
  private readonly sourceChoices = new Map<string, OutgoingSource>();

  /**
   * `${filingId}:${documentId}:${source}` keys whose file upload committed this
   * session. `isReady` cannot stand in for it: it is already true from the
   * original rendering, so Upload / Sign-and-attach would otherwise pass the
   * Continue gate with no file.
   */
  private readonly committedUploads = signal<ReadonlySet<string>>(new Set());

  /** Starts a new session when the loaded filing changes; ignores a missing id. */
  bind(filingId: string | null): void {
    if (filingId != null && filingId !== this.filingId) {
      this.sourceChoices.clear();
      this.committedUploads.set(new Set());
      this.filingId = filingId;
    }
  }

  /**
   * The Upload or Sign-and-attach source the judge picked for this document
   * during the current filing. Null when they have not picked one, including
   * after the page-load Sign default.
   */
  sourceChoice(docId: number | null): OutgoingSource | null {
    return this.sourceChoices.get(this.key(docId)) ?? null;
  }

  /** Records an explicit Upload or Sign-and-attach choice for this document. */
  rememberSourceChoice(docId: number | null, source: OutgoingSource): void {
    this.sourceChoices.set(this.key(docId), source);
  }

  /** Drops a remembered choice so the next open of this document starts on Sign. */
  clearSourceChoice(docId: number | null): void {
    this.sourceChoices.delete(this.key(docId));
  }

  /** Records that the judge's file for this document and source was committed. Signal-backed so the Continue gate recomputes. */
  markUploadCommitted(docId: number | null, source: OutgoingSource): void {
    const key = `${this.key(docId)}:${source}`;
    this.committedUploads.update(keys => new Set(keys).add(key));
  }

  /** Forgets a committed upload (file cleared, re-picked or failed). */
  clearUploadCommitted(docId: number | null, source: OutgoingSource): void {
    const key = `${this.key(docId)}:${source}`;
    if (!this.committedUploads().has(key)) {
      return;
    }
    this.committedUploads.update(keys => {
      const next = new Set(keys);
      next.delete(key);
      return next;
    });
  }

  hasCommittedUpload(docId: number | null, source: OutgoingSource): boolean {
    return this.committedUploads().has(`${this.key(docId)}:${source}`);
  }

  private key(docId: number | null): string {
    return `${this.filingId ?? ''}:${docId ?? ''}`;
  }
}
